import { inject, injectable } from "@launchtray/tsyringe-async";
import { IEditPriceController } from "./IEditPriceController";
import { ReservoirSDK } from "by/gamefactory/web3/utils/ReservoirSDK";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { IEditPriceModel } from "../models/IEditPriceModel";
import { IContextResolver } from "by/gamefactory/cityversetycoon/composition/IContextResolver";
import { SceneManager } from "by/gamefactory/scene/SceneManager";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { CancellationToken } from "async/CancellationToken";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { GameInfo, Stage } from "../../../../../analytics/GameInfo";
import { CongratulationsPopupSceneFactory, WarningMessagePopupSceneFactory } from "../EditPriceModuleFlowExecutor";

@injectable()
export class EditPriceController implements IEditPriceController {

    private readonly contextResolver: IContextResolver;
    private readonly sceneManager: SceneManager;
    private readonly web3Model: Web3Model;
    private readonly editPriceModel: IEditPriceModel;
    private readonly playerModel: IPlayerModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;
    private readonly gameInfo: GameInfo;

    public constructor(
        @inject("IContextResolver") contextResolver: IContextResolver,
        sceneManager: SceneManager,
        web3Model: Web3Model,
        @inject("IEditPriceModel") editPriceModel: IEditPriceModel,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger,
        gameInfo: GameInfo
    ) {
        this.contextResolver = contextResolver;
        this.sceneManager = sceneManager;
        this.web3Model = web3Model;
        this.editPriceModel = editPriceModel;
        this.playerModel = playerModel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
        this.gameInfo = gameInfo;
    }

    public async listBuildingOnMarketplace(buildingId: string, tokenId: string, priceInUsd: number): Promise<void> {
        const totalPrice = ReservoirSDK.toUsdc(priceInUsd);
        const reservoirOrderId = await this.web3Model.listInToReservoir(BigInt(tokenId), totalPrice.toString());
        await this.editPriceModel.addBuildingToMarketplace(buildingId, priceInUsd, reservoirOrderId);
        await this.playerModel.loadBuildings();
    }

    public async editBuildingPriceOnMarketplace(mpBuildingId: string, orderId: string, tokenId: string, newPriceInUsd: number): Promise<boolean> {
        let result = false;
        try {
            this.asyncOperationNotification.show();
            const totalPrice = ReservoirSDK.toUsdc(newPriceInUsd);
            const newOrderId = await this.web3Model.listInToReservoir(BigInt(tokenId), totalPrice.toString());
            result = await this.editPriceModel.editBuildingPriceOnMarketplace(mpBuildingId, newOrderId, newPriceInUsd);
            await this.web3Model.removeFromReservoir(orderId);
            return result;
        } catch (error: unknown) {
            this.logger.error(error);
            return result;
        }
        finally {
            this.asyncOperationNotification.hide();
        }
    }

    public async showCongratulationPopup(isAddToMarketplace: boolean, cancellationToken: CancellationToken): Promise<void> {
        try {
            const sceneFactory: CongratulationsPopupSceneFactory = await this.contextResolver.resolve("CongratulationsPopupSceneFactory");
            const scene = await sceneFactory(isAddToMarketplace, cancellationToken);
            await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.logger.error("Exception on showing CongratulationPopup: ", e);
        }
    }

    public async showWarningMessagePopup(): Promise<boolean> {
        try {
            const sceneFactory: WarningMessagePopupSceneFactory = await this.contextResolver.resolve("WarningMessagePopupSceneFactory");
            const scene = await sceneFactory();
            return await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.logger.error("Exception on showing WarningMessagePopup: ", e);
            return false;
        }
    }
}
